import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { usePerms } from 'contexts/PermsContext';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparisionUtils';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibilityUtils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import DeviceMap from '../features/DeviceMap/index.web';

const FloodMapWidget = () => {
  const perms = usePerms();
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const floodDevices = useMemo(() => {
    if (!connectedDevices?.length) return [];
    return connectedDevices
      .map((device) => ({ device }))
      .filter(
        ({ device }) =>
          (DeviceIsSystemType(device, 'Flood') && DeviceIsDeviceType(device, 'Stage')) ||
          (DeviceIsSystemType(device, 'USGS') &&
            DeviceIsDeviceType(device, 'MonitoringStation') &&
            canViewDeviceType(device, perms, visibleDeviceTypes)),
      )
      .sort((a, b) => parseInt(a.device.node_id || 0, 10) - parseInt(b.device.node_id || 0, 10));
  }, [connectedDevices, perms, visibleDeviceTypes]);

  const deviceOptions = useMemo(
    () =>
      floodDevices.map(({ device }) => ({
        value: device.id,
        label: `${device.static_id ? `${device.static_id} ` : ''}${device.alias}`,
      })),
    [floodDevices],
  );

  // Keep the selection pointing at a device that is still in the list.
  useEffect(() => {
    if (floodDevices.length === 0) {
      setSelectedDevice(null);
      return;
    }

    const stillListed =
      selectedDevice && floodDevices.some(({ device }) => device.id === selectedDevice.id);
    if (!stillListed) setSelectedDevice(floodDevices[0].device);
  }, [floodDevices, selectedDevice]);

  const handleDeviceChange = (deviceId) => {
    const selected = floodDevices.find(({ device }) => device.id === deviceId);
    if (!selected) return;
    setSelectedDevice(selected.device);
  };

  return (
    <div className="d-flex flex-column h-100 w-100 min-h-0 overflow-hidden">
      <div className="flex-grow-1 min-h-0 overflow-hidden rounded-3">
        <DeviceMap
          deviceTypeToFilter="stage"
          enableFilters={false}
          selectedDeviceOverride={selectedDevice}
          setUseThisDevice={setSelectedDevice}
        />
      </div>
      <div className="mt-2">
        <BaseSelect
          isSearchable
          selectProps={{
            'aria-label': 'Flood device',
            options: deviceOptions,
            value: findSelectOption(deviceOptions, selectedDevice?.id),
            placeholder: 'Select',
            isDisabled: deviceOptions.length === 0,
            onChange: (option) => handleDeviceChange(option?.value),
          }}
        />
      </div>
    </div>
  );
};

export default FloodMapWidget;
